import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnFiltersState, ColumnOrderState, GroupingState, OnChangeFn, PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';

// Filters, sort, grouping, search and page live in the URL so a view can be
// bookmarked or sent to a colleague. Column layout preferences stay local.
//
// Filter encoding per column (f.<id>=…): facet `a|b|c`, range `min..max`, text `~words`.

const FILTER_PREFIX = 'f.';

const parseFilters = (params: URLSearchParams): ColumnFiltersState => {
  const out: ColumnFiltersState = [];
  params.forEach((raw, key) => {
    if (!key.startsWith(FILTER_PREFIX)) return;
    const id = key.slice(FILTER_PREFIX.length);
    if (raw.startsWith('~')) {
      out.push({ id, value: raw.slice(1) });
    } else if (raw.includes('..')) {
      const [a, b] = raw.split('..');
      out.push({ id, value: [a === '' ? undefined : Number(a), b === '' ? undefined : Number(b)] });
    } else {
      out.push({ id, value: raw.split('|').filter(Boolean) });
    }
  });
  return out;
};

const encodeFilter = (value: unknown): string | null => {
  if (typeof value === 'string') return value ? `~${value}` : null;
  if (Array.isArray(value) && value.length === 2 && value.every((x) => x === undefined || typeof x === 'number')) {
    const [a, b] = value as [number | undefined, number | undefined];
    return a === undefined && b === undefined ? null : `${a ?? ''}..${b ?? ''}`;
  }
  if (Array.isArray(value) && value.length) return (value as string[]).join('|');
  return null;
};

const parseSorting = (raw: string | null): SortingState =>
  raw
    ? raw.split(',').filter(Boolean).map((part) => {
        const [id, dir] = part.split(':');
        return { id, desc: dir === 'desc' };
      })
    : [];

const resolve = <T,>(updater: T | ((old: T) => T), old: T): T =>
  typeof updater === 'function' ? (updater as (o: T) => T)(old) : updater;

export const useTableUrlState = () => {
  const [params, setParams] = useSearchParams();

  const globalFilter = params.get('q') ?? '';
  const columnFilters = useMemo(() => parseFilters(params), [params]);
  const sorting = useMemo(() => parseSorting(params.get('sort')), [params]);
  const grouping = useMemo<GroupingState>(() => (params.get('group') ? [params.get('group') as string] : []), [params]);
  const pageIndex = Math.max(0, Number(params.get('page') ?? 1) - 1);

  const update = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const setGlobalFilter = useCallback(
    (value: string) => update((p) => {
      if (value) p.set('q', value); else p.delete('q');
      p.delete('page');
    }),
    [update]
  );

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      const next = resolve(updater, columnFilters);
      update((p) => {
        Array.from(p.keys()).forEach((k) => k.startsWith(FILTER_PREFIX) && p.delete(k));
        next.forEach((f) => {
          const enc = encodeFilter(f.value);
          if (enc !== null) p.set(FILTER_PREFIX + f.id, enc);
        });
        p.delete('page');
      });
    },
    [columnFilters, update]
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const next = resolve(updater, sorting);
      update((p) => {
        if (next.length) p.set('sort', next.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(','));
        else p.delete('sort');
      });
    },
    [sorting, update]
  );

  const onGroupingChange: OnChangeFn<GroupingState> = useCallback(
    (updater) => {
      const next = resolve(updater, grouping);
      update((p) => {
        if (next[0]) p.set('group', next[0]); else p.delete('group');
        p.delete('page');
      });
    },
    [grouping, update]
  );

  const setPageIndex = useCallback(
    (index: number) => update((p) => {
      if (index > 0) p.set('page', String(index + 1)); else p.delete('page');
    }),
    [update]
  );

  const clearAll = useCallback(() => update((p) => {
    Array.from(p.keys()).forEach((k) => p.delete(k));
  }), [update]);

  return { globalFilter, setGlobalFilter, columnFilters, onColumnFiltersChange, sorting, onSortingChange, grouping, onGroupingChange, pageIndex, setPageIndex, clearAll };
};

// Column layout + density + page size, remembered per browser.
export interface TablePrefs {
  columnVisibility: VisibilityState;
  columnSizing: Record<string, number>;
  columnOrder: ColumnOrderState;
  density: 'comfortable' | 'compact';
  pageSize: number;
}

const PREFS_KEY = 'outpost.catalogue.prefs.v4';

export const useTablePrefs = (defaults: TablePrefs) => {
  const [prefs, setPrefs] = useState<TablePrefs>(() => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable */
    }
  }, [prefs]);

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    (updater) => setPrefs((old) => ({ ...old, columnVisibility: resolve(updater, old.columnVisibility) })),
    []
  );
  const onColumnSizingChange: OnChangeFn<Record<string, number>> = useCallback(
    (updater) => setPrefs((old) => ({ ...old, columnSizing: resolve(updater, old.columnSizing) })),
    []
  );
  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    (updater) => setPrefs((old) => ({ ...old, columnOrder: resolve(updater, old.columnOrder) })),
    []
  );
  const onPaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState), current: PaginationState, setPageIndex: (i: number) => void) => {
      const next = resolve(updater, current);
      if (next.pageSize !== current.pageSize) setPrefs((old) => ({ ...old, pageSize: next.pageSize }));
      if (next.pageIndex !== current.pageIndex) setPageIndex(next.pageIndex);
    },
    []
  );
  const setDensity = useCallback((density: TablePrefs['density']) => setPrefs((old) => ({ ...old, density })), []);
  const resetLayout = useCallback(() => setPrefs(defaults), [defaults]);

  return { prefs, onColumnVisibilityChange, onColumnSizingChange, onColumnOrderChange, onPaginationChange, setDensity, resetLayout };
};
